import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, Sparkles, Check, LayoutGrid, MessageCircleHeart, Activity, Loader2, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Shell from "../components/Shell";
import { ListenButton } from "../components/ListenButton";
import DailyPulse from "../components/DailyPulse";

const TAGS = ["Creative", "Social", "Work Stress", "Low Energy", "Focused", "Restless",
  "Connected", "Isolated", "Inspired", "Anxious", "Peaceful", "Tired", "Motivated", "Overwhelmed"];

function Dots({ value, onChange, label, testid }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-2">{label}</p>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((v) => (
          <button key={v} data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
            className={`h-9 w-9 rounded-full border transition-colors ${v <= value ? "bg-primary border-primary" : "border-border hover:border-primary/50"}`} />
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const { profile } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [streak, setStreak] = useState({ streak: 0, checked_in_today: false });
  const [mood, setMood] = useState(0);
  const [energy, setEnergy] = useState(0);
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [patterns, setPatterns] = useState({ observations: [], checkin_count: 0 });
  const [genPat, setGenPat] = useState(false);

  const pid = profile?.id;

  useEffect(() => {
    if (!pid) return;
    (async () => {
      try {
        const [p, s, pat, today] = await Promise.all([
          api.get(`/profiles/${pid}/daily-prompt`),
          api.get(`/profiles/${pid}/streak`),
          api.get(`/profiles/${pid}/patterns`),
          api.get(`/profiles/${pid}/pattern-checkins/today`),
        ]);
        setPrompt(p.data.prompt);
        setStreak(s.data);
        setPatterns(pat.data);
        if (today.data) { setMood(today.data.mood_rating || 0); setEnergy(today.data.energy_rating || 0); setTags(today.data.context_tags || []); }
      } catch (e) { console.error("Home data load failed:", e); } finally { setLoading(false); }
    })();
  }, [pid]);

  if (profile === null) return <Navigate to="/setup" replace />;
  if (profile === undefined) return null;

  const toggleTag = (t) => setTags((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : (cur.length < 3 ? [...cur, t] : cur));

  const doCheckin = async () => {
    setSaving(true);
    try {
      await api.post(`/profiles/${pid}/checkin`, { mood_rating: mood || null, energy_rating: energy || null, context_tags: tags });
      const { data } = await api.get(`/profiles/${pid}/streak`);
      setStreak(data);
      toast.success("Checked in. Your companion noticed.");
    } catch { toast.error("Could not save check-in"); } finally { setSaving(false); }
  };

  const generatePatterns = async () => {
    setGenPat(true);
    try {
      const { data } = await api.post(`/profiles/${pid}/patterns/generate`);
      setPatterns((p) => ({ ...p, observations: data.observations }));
      toast.success("Patterns detected");
    } catch (e) { toast.error(e.response?.data?.detail || "Need at least 5 mood check-ins"); } finally { setGenPat(false); }
  };

  return (
    <Shell>
      {loading ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div> : (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="panel p-8 flex-1">
              <p className="overline mb-3 flex items-center gap-2"><Sparkles size={14} /> Today's reflection</p>
              <p className="font-serif text-3xl leading-snug">{prompt}</p>
              {prompt && <ListenButton text={prompt} testid="listen-daily-prompt" className="mt-3" />}
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="panel p-8 md:w-64 flex flex-col items-center justify-center text-center">
              <Flame className="text-accent mb-2" size={28} />
              <div className="font-serif text-5xl text-primary" data-testid="streak-count">{streak.streak}</div>
              <p className="text-sm text-muted-foreground">day streak</p>
            </motion.div>
          </div>

          <DailyPulse profileId={pid} />

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="panel p-8">
            <p className="overline mb-5">Daily check-in</p>
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <Dots value={mood} onChange={setMood} label="How is your mood?" testid="mood" />
              <Dots value={energy} onChange={setEnergy} label="How is your energy?" testid="energy" />
            </div>
            <p className="text-xs text-muted-foreground mb-2">Context (up to 3)</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {TAGS.map((t) => (
                <button key={t} data-testid={`tag-${t.replace(/\s/g, "-").toLowerCase()}`} onClick={() => toggleTag(t)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tags.includes(t) ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/40"}`}>{t}</button>
              ))}
            </div>
            <button data-testid="checkin-btn" onClick={doCheckin} disabled={saving}
              className="gold-btn px-6 py-3 rounded-full font-medium flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} {streak.checked_in_today ? "Update today's check-in" : "Check in"}
            </button>
          </motion.div>

          <div className="panel p-8">
            <div className="flex items-center justify-between mb-4">
              <p className="overline flex items-center gap-2"><Activity size={14} /> Patterns your companion sees</p>
              <button data-testid="generate-patterns-btn" onClick={generatePatterns} disabled={genPat}
                className="text-sm text-primary flex items-center gap-1 disabled:opacity-50">
                {genPat ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Detect ({patterns.checkin_count})
              </button>
            </div>
            {patterns.observations.length ? (
              <ul className="space-y-3">
                {patterns.observations.map((o, i) => (
                  <li key={o.id || o.observation_text || i} data-testid={`pattern-${i}`} className="text-sm text-foreground/90 border-l-2 border-primary/50 pl-4">{o.observation_text}</li>
                ))}
              </ul>
            ) : <p className="text-sm text-muted-foreground">Log mood check-ins for a few days — after 5, your companion will surface patterns across your systems.</p>}
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { to: "/dashboard", icon: LayoutGrid, t: "Your Blueprint", d: "The full unified portrait" },
              { to: "/companion", icon: MessageCircleHeart, t: "Talk to companion", d: "Explore a live dilemma" },
              { to: "/community", icon: Activity, t: "Community", d: "See your type in the world" },
            ].map((c) => (
              <Link key={c.to} to={c.to} className="panel p-6 hover:border-primary/40 transition-colors">
                <c.icon className="text-primary mb-3" size={20} />
                <h3 className="font-serif text-xl">{c.t}</h3>
                <p className="text-sm text-muted-foreground">{c.d}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}
